var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import './CookieConsent.css';
var CookieConsent = function () {
    var _a = useState(false), showBanner = _a[0], setShowBanner = _a[1];
    var _b = useState(false), showModal = _b[0], setShowModal = _b[1];
    var _c = useState({
        essential: true,
        functional: false,
        analytics: false,
        marketing: false
    }), preferences = _c[0], setPreferences = _c[1];
    useEffect(function () {
        // Check if user has already made a choice
        var cookieConsent = getCookie('cookie_consent');
        if (cookieConsent) {
            try {
                // Load existing preferences
                var savedPreferences = JSON.parse(cookieConsent);
                // Validate preferences structure
                if (isValidPreferences(savedPreferences)) {
                    setPreferences(savedPreferences);
                    // Apply existing preferences
                    applyCookiePreferences(savedPreferences);
                }
                else {
                    // Invalid preferences, show banner again
                    console.warn('Invalid cookie preferences found, resetting to defaults');
                    deleteCookie('cookie_consent');
                    setShowBanner(true);
                }
            }
            catch (e) {
                console.warn('Invalid cookie consent data:', e);
                // Clear invalid cookie and show banner
                deleteCookie('cookie_consent');
                setShowBanner(true);
            }
        }
        else {
            // Show banner after a short delay
            var timer_1 = setTimeout(function () {
                setShowBanner(true);
            }, 1000);
            return function () { return clearTimeout(timer_1); };
        }
    }, []);
    // Validate cookie preferences structure
    var isValidPreferences = function (prefs) {
        return (typeof prefs === 'object' &&
            prefs !== null &&
            typeof prefs.essential === 'boolean' &&
            typeof prefs.functional === 'boolean' &&
            typeof prefs.analytics === 'boolean' &&
            typeof prefs.marketing === 'boolean');
    };
    // Apply cookie preferences when they change
    useEffect(function () {
        applyCookiePreferences(preferences);
    }, [preferences]);
    // Apply cookie preferences function
    var applyCookiePreferences = function (prefs) {
        if (prefs.functional === false) {
            disableFunctionalCookies();
        }
        else {
            enableFunctionalCookies();
        }
        if (prefs.analytics === false) {
            disableAnalyticsCookies();
        }
        else {
            enableAnalyticsCookies();
        }
        if (prefs.marketing === false) {
            disableMarketingCookies();
        }
        else {
            enableMarketingCookies();
        }
    };
    var setCookie = function (name, value, days) {
        var expires = new Date();
        expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
        // Add security attributes based on environment
        var isSecure = window.location.protocol === 'https:';
        var secureFlag = isSecure ? ';Secure' : '';
        document.cookie = "".concat(name, "=").concat(value, ";expires=").concat(expires.toUTCString(), ";path=/;SameSite=Lax").concat(secureFlag);
    };
    var getCookie = function (name) {
        var nameEQ = name + '=';
        var ca = document.cookie.split(';');
        for (var i = 0; i < ca.length; i++) {
            var c = ca[i];
            while (c.charAt(0) === ' ')
                c = c.substring(1, c.length);
            if (c.indexOf(nameEQ) === 0)
                return c.substring(nameEQ.length, c.length);
        }
        return null;
    };
    var handleAcceptAll = function () {
        var newPreferences = {
            essential: true,
            functional: true,
            analytics: true,
            marketing: true
        };
        setPreferences(newPreferences);
        savePreferences(newPreferences);
        setShowBanner(false);
    };
    var handleRejectNonEssential = function () {
        var newPreferences = {
            essential: true,
            functional: false,
            analytics: false,
            marketing: false
        };
        setPreferences(newPreferences);
        savePreferences(newPreferences);
        setShowBanner(false);
    };
    var handleCustomize = function () {
        setShowModal(true);
    };
    var handleSavePreferences = function () {
        savePreferences(preferences);
        setShowModal(false);
        setShowBanner(false);
    };
    var handleAcceptAllFromModal = function () {
        var newPreferences = {
            essential: true,
            functional: true,
            analytics: true,
            marketing: true
        };
        setPreferences(newPreferences);
        savePreferences(newPreferences);
        setShowModal(false);
        setShowBanner(false);
    };
    var savePreferences = function (prefs) {
        setCookie('cookie_consent', JSON.stringify(prefs), 365);
        // Apply preferences directly without triggering state update loop
        applyCookiePreferences(prefs);
        // Dispatch custom event for other components
        window.dispatchEvent(new CustomEvent('cookiePreferencesChanged', {
            detail: prefs
        }));
    };
    // Cookie control functions
    var enableFunctionalCookies = function () {
        // Re-enable theme toggle and other functional features
        console.log('Functional cookies enabled');
    };
    var disableFunctionalCookies = function () {
        // Clear theme preference from localStorage
        localStorage.removeItem('theme');
        // Reset to system preference
        var systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        var defaultTheme = systemPrefersDark ? 'dark' : 'light';
        // Apply default theme
        document.documentElement.setAttribute('data-theme', defaultTheme);
        document.body.setAttribute('data-theme', defaultTheme);
        console.log('Functional cookies disabled - theme reset to system preference');
    };
    var enableAnalyticsCookies = function () {
        // Enable analytics tracking (placeholder for future implementation)
        if (window.gtag) {
            window.gtag('consent', 'update', {
                analytics_storage: 'granted'
            });
        }
        console.log('Analytics tracking enabled');
    };
    var disableAnalyticsCookies = function () {
        // Remove analytics cookies with comprehensive patterns
        var analyticsCookies = ['_ga', '_gid', '_gat', 'AMP_TOKEN'];
        analyticsCookies.forEach(function (cookieName) { return deleteCookie(cookieName); });
        // Handle _gac_ prefixed cookies
        document.cookie.split(';').forEach(function (cookie) {
            var cookieName = cookie.split('=')[0].trim();
            if (cookieName.startsWith('_gac_')) {
                deleteCookie(cookieName);
            }
        });
        // Disable analytics tracking
        if (window.gtag) {
            window.gtag('consent', 'update', {
                analytics_storage: 'denied'
            });
        }
        console.log('Analytics tracking disabled');
    };
    var enableMarketingCookies = function () {
        // Enable marketing tracking (placeholder for future implementation)
        if (window.fbq) {
            window.fbq('consent', 'grant');
        }
        console.log('Marketing tracking enabled');
    };
    var disableMarketingCookies = function () {
        // Remove marketing cookies
        var marketingCookies = ['_fbp', '_fbc', 'fr'];
        marketingCookies.forEach(function (cookieName) { return deleteCookie(cookieName); });
        // Disable marketing tracking
        if (window.fbq) {
            window.fbq('consent', 'revoke');
        }
        console.log('Marketing tracking disabled');
    };
    // Utility function to delete cookies
    var deleteCookie = function (name) {
        var hostname = window.location.hostname;
        var domain = hostname.startsWith('www.') ? hostname.substring(4) : hostname;
        // Delete cookie with various domain and path combinations
        var deletePaths = [
            { domain: '', path: '/' },
            { domain: '', path: '' },
            { domain: hostname, path: '/' },
            { domain: '.' + domain, path: '/' },
            { domain: domain, path: '/' }
        ];
        deletePaths.forEach(function (_a) {
            var domainAttr = _a.domain, path = _a.path;
            var domainStr = domainAttr ? "; domain=".concat(domainAttr) : '';
            var pathStr = path ? "; path=".concat(path) : '';
            document.cookie = "".concat(name, "=; expires=Thu, 01 Jan 1970 00:00:00 GMT").concat(domainStr).concat(pathStr);
        });
    };
    var handleCloseBanner = function () {
        setShowBanner(false);
    };
    var handleCloseModal = function () {
        setShowModal(false);
    };
    // Utility function to audit current cookies
    var auditCookies = function () {
        return document.cookie
            .split(';')
            .map(function (cookie) { return cookie.split('=')[0].trim(); })
            .filter(function (name) { return name && name !== 'cookie_consent'; });
    };
    // Function to clear all non-essential cookies
    var clearAllNonEssentialCookies = function () {
        var currentCookies = auditCookies();
        var essentialCookies = ['session', 'csrf', 'auth', 'cookie_consent'];
        currentCookies.forEach(function (cookieName) {
            var isEssential = essentialCookies.some(function (essential) {
                return cookieName.toLowerCase().includes(essential.toLowerCase());
            });
            if (!isEssential) {
                deleteCookie(cookieName);
            }
        });
    };
    if (!showBanner && !showModal)
        return null;
    return (_jsxs(_Fragment, { children: [showBanner && (_jsxs("div", __assign({ className: "cookie-banner", role: "dialog", "aria-labelledby": "cookieTitle", "aria-describedby": "cookieDescription" }, { children: [_jsxs("div", __assign({ className: "cookie-banner-content" }, { children: [_jsxs("div", __assign({ className: "cookie-banner-text" }, { children: [_jsxs("h3", __assign({ id: "cookieTitle", className: "cookie-banner-title" }, { children: [_jsx("i", { className: "fas fa-cookie-bite" }), "We Use Cookies"] })), _jsxs("p", __assign({ id: "cookieDescription", className: "cookie-banner-description" }, { children: ["We use cookies to enhance your experience, provide authentication, and analyze site usage. By continuing to use our site, you agree to our use of cookies.", _jsx("a", __assign({ href: "/cookie-policy", className: "cookie-link" }, { children: " Learn more about our cookie policy" }))] }))] })), _jsxs("div", __assign({ className: "cookie-banner-actions" }, { children: [_jsx("button", __assign({ onClick: handleAcceptAll, className: "btn btn-primary cookie-btn" }, { children: "Accept All Cookies" })), _jsx("button", __assign({ onClick: handleRejectNonEssential, className: "btn btn-secondary cookie-btn" }, { children: "Reject Non-Essential" })), _jsx("button", __assign({ onClick: handleCustomize, className: "btn btn-outline cookie-btn" }, { children: "Customize" }))] }))] })), _jsx("button", __assign({ onClick: handleCloseBanner, className: "cookie-banner-close", "aria-label": "Close cookie banner" }, { children: _jsx("i", { className: "fas fa-times" }) }))] }))), showModal && (_jsx("div", __assign({ className: "cookie-modal", role: "dialog", "aria-labelledby": "cookieModalTitle", "aria-hidden": "false" }, { children: _jsxs("div", __assign({ className: "cookie-modal-content" }, { children: [_jsxs("div", __assign({ className: "cookie-modal-header" }, { children: [_jsx("h2", __assign({ id: "cookieModalTitle", className: "cookie-modal-title" }, { children: "Cookie Preferences" })), _jsx("button", __assign({ onClick: handleCloseModal, className: "cookie-modal-close", "aria-label": "Close modal" }, { children: _jsx("i", { className: "fas fa-times" }) }))] })), _jsxs("div", __assign({ className: "cookie-modal-body" }, { children: [_jsxs("div", __assign({ className: "cookie-category" }, { children: [_jsxs("div", __assign({ className: "cookie-category-header" }, { children: [_jsx("h3", { children: "Essential Cookies" }), _jsxs("label", __assign({ className: "cookie-toggle" }, { children: [_jsx("input", { type: "checkbox", checked: true, disabled: true }), _jsx("span", { className: "cookie-toggle-slider" })] }))] })), _jsx("p", __assign({ className: "cookie-category-description" }, { children: "These cookies are necessary for the website to function and cannot be disabled. They include authentication and security features." }))] })), _jsxs("div", __assign({ className: "cookie-category" }, { children: [_jsxs("div", __assign({ className: "cookie-category-header" }, { children: [_jsx("h3", { children: "Functional Cookies" }), _jsxs("label", __assign({ className: "cookie-toggle" }, { children: [_jsx("input", { type: "checkbox", id: "functionalCookies", checked: preferences.functional, onChange: function (e) { return setPreferences(function (prev) { return (__assign(__assign({}, prev), { functional: e.target.checked })); }); } }), _jsx("span", { className: "cookie-toggle-slider" })] }))] })), _jsx("p", __assign({ className: "cookie-category-description" }, { children: "These cookies remember your preferences and settings, such as theme choice." }))] })), _jsxs("div", __assign({ className: "cookie-category" }, { children: [_jsxs("div", __assign({ className: "cookie-category-header" }, { children: [_jsx("h3", { children: "Analytics Cookies" }), _jsxs("label", __assign({ className: "cookie-toggle" }, { children: [_jsx("input", { type: "checkbox", id: "analyticsCookies", checked: preferences.analytics, onChange: function (e) { return setPreferences(function (prev) { return (__assign(__assign({}, prev), { analytics: e.target.checked })); }); } }), _jsx("span", { className: "cookie-toggle-slider" })] }))] })), _jsx("p", __assign({ className: "cookie-category-description" }, { children: "These cookies help us understand how visitors use our website (currently not implemented)." }))] })), _jsxs("div", __assign({ className: "cookie-category" }, { children: [_jsxs("div", __assign({ className: "cookie-category-header" }, { children: [_jsx("h3", { children: "Marketing Cookies" }), _jsxs("label", __assign({ className: "cookie-toggle" }, { children: [_jsx("input", { type: "checkbox", id: "marketingCookies", checked: preferences.marketing, onChange: function (e) { return setPreferences(function (prev) { return (__assign(__assign({}, prev), { marketing: e.target.checked })); }); } }), _jsx("span", { className: "cookie-toggle-slider" })] }))] })), _jsx("p", __assign({ className: "cookie-category-description" }, { children: "These cookies are used to deliver relevant advertisements (currently not implemented)." }))] }))] })), _jsxs("div", __assign({ className: "cookie-modal-footer" }, { children: [_jsx("button", __assign({ onClick: handleSavePreferences, className: "btn btn-primary" }, { children: "Save Preferences" })), _jsx("button", __assign({ onClick: handleAcceptAllFromModal, className: "btn btn-secondary" }, { children: "Accept All" }))] }))] })) })))] }));
};
export default CookieConsent;
